/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog for destructive actions.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * ConfirmDialog - Modal confirmation dialog
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Handler for open state changes
 * @param {string} props.title - Dialog title
 * @param {string} props.description - Dialog description
 * @param {Function} props.onConfirm - Callback when confirmed
 * @param {string} [props.confirmText] - Confirm button text
 * @param {string} [props.cancelText] - Cancel button text
 * @param {boolean} [props.destructive] - Whether this is a destructive action
 * @param {boolean} [props.loading] - Whether confirm action is in progress
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = true,
  loading = false,
}) {
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {destructive && (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            )}
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Please wait...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook for managing confirm dialog state
 */
export function useConfirmDialog() {
  const [dialogState, setDialogState] = React.useState({
    open: false,
    title: '',
    description: '',
    confirmText: 'Confirm',
    destructive: true,
  });
  const resolveRef = React.useRef(null);

  const confirm = React.useCallback(({
    title,
    message,
    description,
    confirmLabel,
    confirmText,
    variant,
    destructive = true,
  }) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        open: true,
        title,
        description: message || description || '',
        confirmText: confirmLabel || confirmText || 'Confirm',
        destructive: variant === 'destructive' || destructive,
      });
    });
  }, []);

  const handleConfirm = React.useCallback(() => {
    setDialogState(prev => ({ ...prev, open: false }));
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleOpenChange = React.useCallback((open) => {
    setDialogState(prev => ({ ...prev, open }));
    if (!open) {
      // Dialog was closed/cancelled
      resolveRef.current?.(false);
      resolveRef.current = null;
    }
  }, []);

  const confirmProps = {
    open: dialogState.open,
    onOpenChange: handleOpenChange,
    title: dialogState.title,
    description: dialogState.description,
    onConfirm: handleConfirm,
    confirmText: dialogState.confirmText,
    destructive: dialogState.destructive,
  };

  return {
    confirmProps,
    confirm,
  };
}

export default ConfirmDialog;
